import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import Helmet from "@/components/base/Helmet";
import { articles, videos } from "@/mocks/data";
import { categories } from "@/mocks/data";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = articles.find((a) => a.id === id);
  const video = videos.find((v) => v.id === id);

  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);
  const relatedVideos = videos.filter((v) => v.id !== id).slice(0, 6);

  const isVideo = !!video;
  const content = article;

  if (!content && !video) {
    return (
      <div className="min-h-screen bg-gray-950">
        <Navbar />
        <main className="pt-20 pb-8">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-gray-800 text-gray-500 mb-4">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h1 className="text-xl text-white font-bold mb-2">内容未找到</h1>
              <p className="text-gray-400 text-sm mb-6">您要查看的内容不存在或已被移除</p>
              <Link
                to="/"
                className="inline-flex items-center px-5 py-2 bg-amber-500 text-black font-semibold text-sm rounded-full hover:bg-amber-400 transition-colors"
              >
                <i className="ri-arrow-left-line mr-1"></i> 返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const pageTitle = isVideo
    ? `${video.title} - 豆花视频 | 免费在线观看`
    : `${content?.title} - 豆花视频`;

  const pageDesc = isVideo
    ? `${video.title}：${video.description} 豆花视频提供${video.title}免费在线观看，高清不缓冲。`
    : content?.summary || "";

  const pageKeywords = isVideo
    ? `${video.title},${video.tags.join(",")},豆花视频,免费在线观看,高清视频`
    : `${content?.tags.join(",")},豆花视频,影视资讯`;

  const structuredData = isVideo
    ? {
        "@context": "https://schema.org",
        "@type": "Movie",
        name: video.title,
        description: video.description,
        image: video.thumbnail,
        dateCreated: video.year,
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: video.rating,
          bestRating: "10",
        },
      }
    : {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: content?.title,
        description: content?.summary,
        image: content?.cover,
        author: {
          "@type": "Person",
          name: content?.author,
        },
        datePublished: content?.publishDate,
      };

  return (
    <>
      <Helmet
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonical={`https://hnkuyin.cn/article/${id}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogImage={isVideo ? video.thumbnail : content?.cover}
        ogType={isVideo ? "video.movie" : "article"}
        scriptLd={structuredData}
      />

      <div className="min-h-screen bg-gray-950">
        <Navbar />

        <main className="pt-20 pb-8">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  ...(isVideo
                    ? [
                        {
                          label: categories.find((c) => c.slug === video.category)?.name || "分类",
                          href: `/category/${video.category}`,
                        },
                      ]
                    : []),
                  { label: isVideo ? video.title : content?.title || "" },
                ]}
              />

              <article>
                <header className="mb-8">
                  <h1 className="text-xl md:text-3xl font-bold text-white leading-tight mb-4">
                    {isVideo ? video.title : content?.title}
                  </h1>

                  <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                    {isVideo ? (
                      <>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-400"></i>
                          {video.rating}
                        </span>
                        <span>{video.year}</span>
                        <span>{video.region}</span>
                        <span className="px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 text-[10px]">
                          {video.updateEpisode}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>{content?.author}</span>
                        <span>{content?.publishDate}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {content && content.viewCount > 10000
                            ? `${(content.viewCount / 10000).toFixed(1)}万`
                            : content?.viewCount}{" "}
                          阅读
                        </span>
                      </>
                    )}
                  </div>

                  {!isVideo && content?.tags && (
                    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                      {content.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-gray-800 text-[10px] text-gray-300"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </header>

                {isVideo ? (
                  <>
                    <div className="relative aspect-video rounded-xl overflow-hidden mb-8 bg-gray-900">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        width={1280}
                        height={720}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                        <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-amber-500/90 text-white shadow-lg shadow-amber-500/30 cursor-pointer hover:bg-amber-400 transition-colors">
                          <i className="ri-play-fill text-2xl md:text-4xl ml-1"></i>
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                        <p className="text-sm text-white font-medium">{video.title}</p>
                      </div>
                    </div>

                    <div className="bg-gray-900/70 border border-gray-800/50 rounded-xl p-5 mb-8">
                      <h2 className="text-base font-bold text-white mb-3">影片简介</h2>
                      <p className="text-sm text-gray-300 leading-relaxed">{video.description}</p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-800/50">
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">年份</p>
                          <p className="text-sm text-white">{video.year}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">地区</p>
                          <p className="text-sm text-white">{video.region}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">评分</p>
                          <p className="text-sm text-amber-400 font-semibold">{video.rating}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-gray-500 mb-0.5">状态</p>
                          <p className="text-sm text-white">{video.updateEpisode}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-gray-800/50">
                        {video.tags.map((tag) => (
                          <span key={tag} className="px-2.5 py-1 rounded-full bg-gray-800 text-xs text-gray-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className="prose prose-invert prose-sm md:prose-base max-w-none mb-8 text-gray-300 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: content?.contentHtml || "" }}
                  />
                )}
              </article>

              {/* Related Content */}
              {isVideo && relatedVideos.length > 0 && (
                <section className="mt-10 pt-8 border-t border-gray-800/50">
                  <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
                    <i className="ri-movie-line text-amber-400"></i>
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="group block rounded-xl overflow-hidden bg-gray-900/80 border border-gray-800/50 hover:border-amber-500/50 transition-all duration-300"
                      >
                        <div className="relative aspect-video overflow-hidden">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            width={640}
                            height={360}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-950/90 via-transparent to-transparent"></div>
                          <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-amber-400">
                            {v.rating}
                          </div>
                        </div>
                        <div className="p-2.5">
                          <h3 className="text-xs font-medium text-white line-clamp-1 group-hover:text-amber-400 transition-colors">
                            {v.title}
                          </h3>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {!isVideo && relatedArticles.length > 0 && (
                <section className="mt-10 pt-8 border-t border-gray-800/50">
                  <h2 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
                    <i className="ri-article-line text-amber-400"></i>
                    相关文章
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((a) => (
                      <Link
                        key={a.id}
                        to={`/article/${a.id}`}
                        className="group block rounded-xl overflow-hidden bg-gray-900/80 border border-gray-800/50 hover:border-amber-500/50 transition-all duration-300"
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={a.cover}
                            alt={a.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            width={800}
                            height={450}
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-medium text-white line-clamp-2 group-hover:text-amber-400 transition-colors">
                            {a.title}
                          </h3>
                          <p className="text-xs text-gray-400 mt-2">{a.publishDate}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}